import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Award, MapPin, Building2, Handshake, Users, Wrench, Lightbulb, Quote, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';

const t = (lang, fr, en) => (lang === 'fr' ? fr : en);

const GciEntrepriseEngageePage = () => {
  const { language } = useLanguage();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const principles = [
    {
      Icon: MapPin,
      title: t(language, 'Un ancrage local fort', 'Strong local roots'),
      text: t(
        language,
        "Nous connaissons Kinshasa : ses communes, ses sols, son foncier, ses acteurs et ses réseaux. Cette proximité fait la différence sur chaque décision technique.",
        'We know Kinshasa: its communes, soils, land status, stakeholders and networks. This proximity makes a difference on every technical decision.'
      ),
    },
    {
      Icon: Wrench,
      title: t(language, 'Un savoir-faire intégré', 'Integrated know-how'),
      text: t(
        language,
        "Bureau d'études, ingénierie structure, coordination de chantier et suivi budgétaire, tout est piloté en interne. Un seul interlocuteur, de l'esquisse à la remise des clés.",
        'Engineering office, structural design, site coordination and budget follow-up are handled in-house. A single point of contact from sketch to keys handover.'
      ),
    },
    {
      Icon: Handshake,
      title: t(language, "Un rapport qualité / prix maîtrisé", 'A controlled quality / price ratio'),
      text: t(
        language,
        "Nous négocions matériaux et prestations avec les meilleurs partenaires locaux et régionaux pour construire durablement, sans faire flamber le budget de nos clients.",
        'We negotiate materials and services with the best local and regional partners to build sustainably, without inflating our clients\u2019 budgets.'
      ),
    },
    {
      Icon: Lightbulb,
      title: t(language, 'Une exigence de qualité', 'A commitment to quality'),
      text: t(
        language,
        "Contrôle du béton, suivi HSE, réception des lots par un conducteur GCI, garanties de parfait achèvement, biennale et décennale : la qualité est vérifiable à chaque étape.",
        'Concrete testing, HSE oversight, lot acceptance by a GCI foreman, one-year, two-year and ten-year warranties: quality is verifiable at every step.'
      ),
    },
  ];

  const kpis = [
    { value: '2016', label: t(language, "Année de création", 'Year founded') },
    { value: '+50', label: t(language, "Projets livrés à Kinshasa", 'Projects delivered in Kinshasa') },
    { value: '+80', label: t(language, "Collaborateurs et sous-traitants", 'Staff and subcontractors') },
    { value: '18+', label: t(language, "Années d'expérience cumulée", 'Years of combined experience') },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-[240px]" data-testid="gci-committed-page">
        {/* Hero */}
        <section className="relative h-[380px] md:h-[460px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                "url('https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/a5uoobne_Capture%20d%E2%80%99%C3%A9cran%202026-07-29%20142823.png')",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
          <div className="relative max-w-6xl mx-auto px-6 h-full flex flex-col justify-center">
            <span className="inline-block text-orange-400 font-semibold tracking-widest text-sm mb-3">
              {t(language, 'GCI · Kinshasa', 'GCI · Kinshasa')}
            </span>
            <h1 className="text-white text-3xl sm:text-4xl md:text-5xl font-bold leading-tight max-w-3xl">
              {t(
                language,
                "GLOBAL CONSTRUCTION & INGÉNIERIE, une entreprise engagée pour la construction en RDC",
                'GLOBAL CONSTRUCTION & INGÉNIERIE, a company committed to construction in DRC'
              )}
            </h1>
            <p className="text-white/85 mt-4 max-w-2xl text-base md:text-lg">
              {t(
                language,
                "Fondée en 2016 par Elie, GCI est une entreprise spécialisée dans le domaine de la construction des bâtiments à Kinshasa.",
                'Founded in 2016 by Elie, GCI is a company specialised in building construction in Kinshasa.'
              )}
            </p>
          </div>
        </section>

        {/* Un acteur incontournable */}
        <section className="py-16 md:py-20" data-testid="pillar-intro">
          <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-block text-orange-500 font-semibold tracking-widest text-sm mb-3">
                {t(language, 'UN ACTEUR ENGAGÉ EN RDC', 'A COMMITTED ACTOR IN DRC')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-5" style={{ color: '#702963' }}>
                {t(
                  language,
                  "Bâtir la ville de demain, à Kinshasa",
                  'Building tomorrow\u2019s city in Kinshasa'
                )}
              </h2>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
                {t(
                  language,
                  "Depuis notre création en 2016, GLOBAL CONSTRUCTION & INGÉNIERIE SARL (GCI) accompagne des particuliers, des entreprises et des institutions dans la conception et la réalisation de leurs bâtiments : villas individuelles, immeubles de rapport, bâtiments professionnels et infrastructures énergétiques."
                  ,
                  'Since our creation in 2016, GLOBAL CONSTRUCTION & INGÉNIERIE SARL (GCI) has supported individuals, businesses and institutions in designing and delivering their buildings: individual villas, rental buildings, commercial premises and energy infrastructure.'
                )}
              </p>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-6">
                {t(
                  language,
                  "Notre stratégie repose sur une conviction : la qualité d\u2019une construction se joue avant même le premier coup de pioche. C\u2019est pourquoi nous investissons dans l\u2019écoute, les études préalables et l\u2019accompagnement humain.",
                  "Our strategy rests on one conviction: the quality of a construction is decided before the first dig. That is why we invest in listening, upstream studies and human support."
                )}
              </p>
              <Link
                to="/qui-sommes-nous/notre-equipe"
                className="inline-flex items-center gap-2 border-2 border-[#702963] text-[#702963] font-semibold px-6 py-3 hover:bg-[#702963] hover:text-white transition-all"
                data-testid="pillar-team-link"
              >
                {t(language, "Rencontrer l'équipe", 'Meet the team')}
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/667zs6dn_IMG_6325.jpg"
                alt="GCI Kinshasa - équipe au travail"
                className="w-full rounded-sm shadow-2xl object-cover aspect-[4/3]"
                loading="lazy"
              />
              <div className="absolute -bottom-5 -left-5 bg-orange-500 text-white p-6 rounded-sm shadow-xl hidden md:block">
                <div className="text-4xl font-bold leading-none">GCI</div>
                <div className="text-xs mt-1 opacity-90 uppercase tracking-widest">
                  {t(language, 'Depuis 2016 · Kinshasa', 'Since 2016 · Kinshasa')}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Le mot du fondateur */}
        <section className="py-16 md:py-20 bg-gray-50" data-testid="founder-word">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold" style={{ color: '#702963' }}>
                {t(language, 'Le mot du fondateur', "The founder's word")}
              </h2>
              <div className="mx-auto mt-3 h-[3px] w-16 bg-orange-500 rounded-full" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-1 flex justify-center">
                <div className="w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden border-4 border-orange-500 shadow-xl">
                  <img
                    src="https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/8j4iqyj3_IMG_5718.jpg"
                    alt="Elie KITULU MASINGA — Fondateur GCI"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <Quote className="w-8 h-8 text-orange-500 mb-3" />
                <p className="text-lg md:text-xl text-gray-800 italic leading-relaxed mb-6">
                  {t(
                    language,
                    "Depuis 2016, nous mettons notre passion du bâtiment au service des familles et des entreprises congolaises. Chaque projet est une aventure humaine autant qu'une prouesse technique — et c'est cette conviction qui guide chaque décision de nos équipes.",
                    'Since 2016, we have put our passion for construction at the service of Congolese families and businesses. Every project is a human adventure as much as a technical feat — and this conviction guides every decision of our teams.'
                  )}
                </p>
                <div className="font-bold text-gray-900">Elie KITULU MASINGA</div>
                <div className="text-sm text-gray-500">
                  {t(language, 'Fondateur & Directeur Gérant — GCI', 'Founder & Managing Director — GCI')}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 4 principes */}
        <section className="py-16 md:py-24" data-testid="principles">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: '#702963' }}>
                {t(
                  language,
                  "Notre stratégie repose sur 4 principes simples et solides",
                  'Our strategy relies on 4 simple and strong principles'
                )}
              </h2>
              <div className="mx-auto h-[3px] w-16 bg-orange-500 rounded-full" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {principles.map((p, i) => {
                const Icon = p.Icon;
                return (
                  <div
                    key={i}
                    className="bg-white border border-gray-200 rounded-sm p-7 md:p-8 shadow-sm hover:shadow-xl transition-all duration-300 flex gap-5"
                    data-testid={`principle-${i}`}
                  >
                    <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center bg-orange-50 rounded-full text-orange-600">
                      <Icon className="w-8 h-8" strokeWidth={2} />
                    </div>
                    <div>
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                        {p.title}
                      </h3>
                      <p className="text-gray-700 text-sm md:text-base leading-relaxed">
                        {p.text}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Constructeur de qualité + partners CTA */}
        <section className="py-16 md:py-20 bg-gray-50" data-testid="quality-partners">
          <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-5" style={{ color: '#702963' }}>
                {t(language, 'Un constructeur de qualité', 'A quality builder')}
              </h2>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
                {t(
                  language,
                  "La construction d'une maison ou d'un bâtiment est le projet d'une vie. C'est pourquoi votre projet mérite la meilleure attention.",
                  'Building a house or a building is a project of a lifetime. That is why your project deserves the best attention.'
                )}
              </p>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-6">
                {t(
                  language,
                  "Nous nous appuyons sur un réseau soigneusement sélectionné de partenaires en énergie solaire, en fournitures et en décoration intérieure, ainsi que sur nos propres équipes formées à nos procédés.",
                  'We rely on a carefully selected network of partners in solar energy, supplies and interior design, along with our own teams trained in our processes.'
                )}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/qui-sommes-nous/nos-partenaires"
                  className="inline-flex items-center gap-2 bg-orange-500 text-white font-semibold px-6 py-3 rounded-sm hover:bg-orange-600 transition-all"
                  data-testid="link-partners"
                >
                  <Handshake size={18} /> {t(language, 'Nos partenaires', 'Our partners')}
                </Link>
                <Link
                  to="/qui-sommes-nous/espace-documents"
                  className="inline-flex items-center gap-2 border-2 border-[#702963] text-[#702963] font-semibold px-6 py-3 hover:bg-[#702963] hover:text-white transition-all"
                  data-testid="link-documents"
                >
                  {t(language, 'Espace documents', 'Documents area')}
                </Link>
              </div>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_construction-global/artifacts/wjm83po0_ChatGPT%20Image%2010%20juil.%202026%2C%2015_33_18.png"
                alt="GCI Quality"
                className="w-full rounded-sm shadow-2xl aspect-[4/3] object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* KPI banner */}
        <section className="py-16 md:py-20 bg-[#702963] text-white" data-testid="kpis">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 text-center">
              {kpis.map((k, i) => (
                <div key={i} className="border-r border-white/15 last:border-r-0">
                  <div className="text-4xl md:text-5xl font-bold text-orange-400">{k.value}</div>
                  <div className="text-sm md:text-base mt-2 opacity-90">{k.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default GciEntrepriseEngageePage;
